import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { formatRupiah } from "@/lib/format";
import { Users, Crown, Ticket, TrendingUp, DollarSign, Car, Plus, Pencil, Archive, Upload, Download, Loader2, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  brand: "", model: "", variant: "", category: "motor_listrik", production_year: "",
  official_price: "", image: "",
  motor_nominal_w: "", motor_peak_w: "", motor_torque_nm: "",
  battery_capacity_kwh: "", battery_voltage_v: "", battery_capacity_ah: "", battery_chemistry: "",
  battery_scheme: "beli", monthly_battery_rental: "", removable_battery: false,
  claimed_range_km: "", tested_range_min_km: "", tested_range_max_km: "", efficiency_wh_per_km: "",
  top_speed_kmh: "", charge_time_hours: "",
  engine_cc: "", fuel_type: "", efficiency_km_per_liter: "", annual_service_cost: "",
  curb_weight_kg: "", maximum_payload_kg: "", length_mm: "", width_mm: "", height_mm: "",
  vehicle_warranty: "", battery_warranty: "", service_coverage: "",
  specification_completeness: "", completeness_manual: false, data_status: "verified",
};

const CORE_ELECTRIC = ["official_price", "motor_nominal_w", "battery_capacity_kwh", "battery_voltage_v", "battery_chemistry", "battery_scheme", "claimed_range_km", "efficiency_wh_per_km", "top_speed_kmh", "charge_time_hours", "curb_weight_kg", "maximum_payload_kg", "vehicle_warranty", "battery_warranty", "service_coverage", "image"];
const CORE_GAS = ["official_price", "engine_cc", "fuel_type", "efficiency_km_per_liter", "top_speed_kmh", "curb_weight_kg", "maximum_payload_kg", "annual_service_cost", "vehicle_warranty", "service_coverage", "image"];
function computeCompleteness(form) {
  const fields = form.category === "motor_bensin" ? CORE_GAS : CORE_ELECTRIC;
  const filled = fields.filter((f) => { const v = form[f]; return v !== "" && v !== null && v !== undefined; }).length;
  return Math.round((filled / fields.length) * 100);
}

const SEGMENT_OPTIONS = [
  ["all", "Semua"], ["registered_only", "Registered Only"], ["trial_1", "Trial 1"],
  ["trial_2_plus", "Trial 2+"], ["three_day_buyer", "3-Day Buyer"], ["premium", "Premium"], ["expired", "Expired"],
];

function FunnelSection({ title, steps, base, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-sm font-semibold text-navy mb-2">{title}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {steps.map((s, i) => {
          const prev = i > 0 ? (steps[i - 1].v ?? 0) : null;
          return (
            <Card key={s.l} className="rounded-2xl p-5" data-testid={`funnel-${s.l}`}>
              <p className="text-sm text-muted-foreground">{s.l}</p>
              <p className="font-heading font-extrabold text-2xl text-navy">{s.v ?? 0}</p>
              {i > 0 && <p className="text-xs font-semibold text-teal mt-1">{prev > 0 ? `↓ ${Math.round(((s.v ?? 0) / prev) * 100)}% dari langkah sebelumnya` : "— (langkah sebelumnya 0)"}</p>}
              {base > 0 && <p className="text-xs text-muted-foreground">{Math.round(((s.v ?? 0) / base) * 100)}% dari registrasi</p>}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const role = user?.role;
  const isSuper = role === "super_admin";
  const isEditor = role === "editor";
  const canManageUsers = ["super_admin", "admin"].includes(role);
  const [roleChange, setRoleChange] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tariffs, setTariffs] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  const [showDeleteDemo, setShowDeleteDemo] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userOrigin, setUserOrigin] = useState("organik");
  const [userCounts, setUserCounts] = useState({ organik: 0, emergent: 0 });
  const [showDeleteEmergent, setShowDeleteEmergent] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [misses, setMisses] = useState([]);
  const [cohortStart, setCohortStart] = useState("");
  const [cohortEnd, setCohortEnd] = useState("");
  const [anomalies, setAnomalies] = useState(null);
  const [userSegment, setUserSegment] = useState("all");
  const [aiDraftQuery, setAiDraftQuery] = useState("");
  const [drafting, setDrafting] = useState(null);
  const aiDraft = async (query) => {
    const text = (query || "").trim();
    if (!text) return;
    setDrafting(query);
    try {
      await api.post("/admin/vehicles/ai-draft", { query: text });
      toast.success("Draft AI dibuat di Arsip (belum terverifikasi). Tinjau di Kendaraan → Arsip, lalu Aktifkan bila sudah benar.");
      setVehicleStatusView("arsip");
      loadVehicles(vehicleSearch, "arsip");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Gagal membuat draft AI.");
    } finally { setDrafting(null); }
  };
  const [uploadingImg, setUploadingImg] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [vehicleStatusView, setVehicleStatusView] = useState("managed");
  const loadVehicles = (q = vehicleSearch, view = vehicleStatusView) => {
    const params = {};
    if (q) params.q = q;
    if (["aktif", "arsip", "dihapus"].includes(view)) params.status = view;
    api.get("/admin/vehicles", { params }).then((r) => setVehicles(r.data.items)).catch(() => {});
  };
  // eslint-disable-next-line
  useEffect(() => { const t = setTimeout(() => loadVehicles(), 300); return () => clearTimeout(t); }, [vehicleSearch, vehicleStatusView]);
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const changeStatus = async (ids, status) => { await api.post("/admin/vehicles/bulk", { vehicle_ids: ids, action: status }); toast.success(status === "dihapus" ? "Kendaraan dihapus" : "Status diperbarui"); setSelected([]); loadAll(); };

  const loadUsers = (origin = userOrigin, segment = userSegment) => {
    const params = {};
    if (origin !== "all") params.origin = origin;
    if (segment !== "all") params.segment = segment;
    api.get("/admin/users", { params }).then((r) => { setUsers(r.data.items); if (r.data.counts) setUserCounts(r.data.counts); }).catch(() => {});
  };
  const deleteEmergent = async () => {
    const { data } = await api.delete("/admin/users/emergent");
    toast.success(`${data.deleted} akun emergent dihapus`);
    setShowDeleteEmergent(false); loadUsers(); api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  // eslint-disable-next-line
  useEffect(() => { if (canManageUsers) loadUsers(); }, [userOrigin, userSegment]);

  const loadFunnel = (cs = cohortStart, ce = cohortEnd) => {
    const params = {};
    if (cs) params.cohort_start = cs;
    if (ce) params.cohort_end = ce;
    api.get("/admin/reports/funnel", { params }).then((r) => setFunnel(r.data)).catch(() => {});
    api.get("/admin/reports/anomalies").then((r) => setAnomalies(r.data)).catch(() => {});
  };

  const loadAll = () => {
    if (!isEditor) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    loadVehicles();
    if (canManageUsers) loadUsers();
    if (isSuper) api.get("/admin/audit-logs").then((r) => setAuditLogs(r.data.items)).catch(() => {});
    if (canManageUsers) {
      loadFunnel();
      api.get("/admin/reports/search-misses").then((r) => setMisses(r.data.items || [])).catch(() => {});
    }
    api.get("/admin/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/meta/tariffs").then((r) => setTariffs(r.data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (v) => { setEditing(v.vehicle_id); setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, v[k] ?? ""])) }); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    const data = {};
    Object.entries(form).forEach(([k, val]) => { if (val !== "" && val !== null) data[k] = val; });
    if (!form.completeness_manual) delete data.specification_completeness;
    try {
      if (editing) await api.put(`/admin/vehicles/${editing}`, { data });
      else await api.post("/admin/vehicles", { data });
      toast.success(editing ? "Kendaraan diperbarui" : "Kendaraan ditambahkan");
      setDialogOpen(false); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const archive = async (id) => {
    await api.delete(`/admin/vehicles/${id}`); toast.success("Diarsipkan"); setArchiveId(null); loadAll();
  };

  const importCsv = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/vehicles/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} ditambahkan, ${data.updated} diperbarui`);
      if (data.errors?.length) toast.warning(`${data.errors.length} baris bermasalah`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    const res = await api.get("/admin/vehicles/csv-template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "template_kendaraan.csv"; a.click();
  };

  const setRole = async (userId, role) => { try { await api.post("/admin/users/role", { user_id: userId, role }); toast.success("Peran diperbarui"); loadAll(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const addTrial = async (userId) => { await api.post("/admin/users/add-trial", { user_id: userId, credits: 3 }); toast.success("+3 trial"); loadAll(); };
  const deleteDemo = async () => { const { data } = await api.post("/admin/vehicles/delete-demo"); toast.success(`${data.deleted} data demo dihapus`); setShowDeleteDemo(false); loadAll(); };
  const uploadImage = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setUploadingImg(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/admin/vehicles/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((fm) => ({ ...fm, image: data.url }));
      toast.success("Gambar berhasil diunggah");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingImg(false); e.target.value = ""; }
  };
  const grantPremium = async (userId, days) => { await api.post("/admin/users/grant-premium", { user_id: userId, days }); toast.success(`Premium +${days} hari`); loadAll(); };

  const saveTariffs = async () => {
    const fuels = {};
    (tariffs.fuels || []).forEach((f) => { fuels[f.fuel_type] = Number(f.price_per_liter); });
    await api.put("/admin/tariffs", { fuels, electricity_price: Number(tariffs.electricity?.price_per_kwh), public_charging_price: Number(tariffs.public_charging?.price_per_kwh) });
    toast.success("Tarif disimpan");
  };
  const savePlan = async (p) => { await api.put("/admin/plans", { plan_id: p.plan_id, price: Number(p.price), duration_days: Number(p.duration_days), label: p.label }); toast.success("Paket disimpan"); };

  const filteredVehicles = vehicles;
  const autoCompleteness = computeCompleteness(form);
  const toggleAll = (checked) => setSelected(checked ? filteredVehicles.map((v) => v.vehicle_id) : []);

  const STAT_CARDS = stats ? [
    { icon: Users, l: "Pengguna Organik", v: stats.total_users, c: "text-blue-500 bg-blue-50" },
    { icon: Crown, l: "Premium Aktif", v: stats.premium_users, c: "text-amber-500 bg-amber-50" },
    { icon: Ticket, l: "Trial Terpakai", v: stats.trials_used, c: "text-teal bg-accent" },
    { icon: TrendingUp, l: "Conversion", v: `${stats.conversion_rate}%`, c: "text-purple-500 bg-purple-50" },
    { icon: DollarSign, l: "Pendapatan", v: formatRupiah(stats.revenue), c: "text-green-500 bg-green-50" },
    { icon: Car, l: "Data Kadaluarsa", v: stats.stale_vehicles, c: "text-red-500 bg-red-50" },
    { icon: Users, l: "Akun Emergent/Uji", v: stats.emergent_users ?? 0, c: "text-amber-600 bg-amber-50" },
  ] : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-page">
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy mb-6">Panel Admin</h1>
      <Tabs defaultValue={isEditor ? "vehicles" : "stats"}>
        <TabsList className="rounded-full bg-secondary p-1 mb-6 flex-wrap h-auto">
          {!isEditor && <TabsTrigger value="stats" data-testid="admin-tab-stats" className="rounded-full px-5">Statistik</TabsTrigger>}
          <TabsTrigger value="vehicles" data-testid="admin-tab-vehicles" className="rounded-full px-5">Kendaraan</TabsTrigger>
          {canManageUsers && <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full px-5">Pengguna</TabsTrigger>}
          {canManageUsers && <TabsTrigger value="reports" data-testid="admin-tab-reports" className="rounded-full px-5">Laporan</TabsTrigger>}
          {isSuper && <TabsTrigger value="audit" data-testid="admin-tab-audit" className="rounded-full px-5">Riwayat Peran</TabsTrigger>}
          {!isEditor && <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-full px-5">Tarif & Paket</TabsTrigger>}
        </TabsList>

        <TabsContent value="stats">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STAT_CARDS.map((s) => (
              <Card key={s.l} className="rounded-2xl p-5 flex items-center gap-4" data-testid={`stat-${s.l}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.c}`}><s.icon className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">{s.l}</p><p className="font-heading font-extrabold text-2xl text-navy">{s.v}</p></div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="vehicles">
          <div className="flex flex-wrap gap-2 mb-4">
            <Button data-testid="admin-add-vehicle" onClick={openNew} className="rounded-full bg-teal hover:bg-teal-hover"><Plus className="w-4 h-4 mr-1" />Tambah</Button>
            <Button data-testid="admin-template" onClick={downloadTemplate} variant="outline" className="rounded-full"><Download className="w-4 h-4 mr-1" />Template CSV</Button>
            <label><input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="admin-csv-input" /><span className="inline-flex items-center cursor-pointer rounded-full border border-border px-4 h-10 text-sm font-medium hover:bg-secondary"><Upload className="w-4 h-4 mr-1" />Impor CSV</span></label>
            <Button data-testid="admin-delete-demo" onClick={() => setShowDeleteDemo(true)} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1" />Hapus Data Demo</Button>
            <Select value={vehicleStatusView} onValueChange={(v) => { setSelected([]); setVehicleStatusView(v); }}>
              <SelectTrigger data-testid="vehicle-status-view" className="rounded-full h-10 w-40 ml-auto"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="managed">Aktif & Arsip</SelectItem>
                <SelectItem value="aktif">Aktif</SelectItem>
                <SelectItem value="arsip">Arsip</SelectItem>
                <SelectItem value="dihapus">Terhapus</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="admin-vehicle-search" value={vehicleSearch} onChange={(e) => setVehicleSearch(e.target.value)} placeholder="Cari merek atau model…" className="rounded-full pl-9 h-10 w-64" />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input data-testid="admin-ai-draft-input" value={aiDraftQuery} onChange={(e) => setAiDraftQuery(e.target.value)} placeholder="Cari data via AI (mis. AIMA Luna)…" className="rounded-full h-10 w-full sm:w-60" />
              <Button data-testid="admin-ai-draft-btn" disabled={!!aiDraftQuery && drafting === aiDraftQuery} onClick={() => aiDraft(aiDraftQuery)} className="rounded-full bg-navy hover:bg-navy/90 text-white whitespace-nowrap">Buat Draft AI</Button>
            </div>
          </div>
          {selected.length > 0 && (
            <div className="flex items-center flex-wrap gap-2 mb-3 bg-accent rounded-xl p-3" data-testid="bulk-bar">
              <span className="text-sm font-semibold text-teal">{selected.length} dipilih</span>
              <Button size="sm" data-testid="bulk-activate" onClick={() => changeStatus(selected, "aktif")} className="rounded-full bg-teal hover:bg-teal-hover">Aktifkan</Button>
              <Button size="sm" variant="outline" data-testid="bulk-archive" onClick={() => changeStatus(selected, "arsip")} className="rounded-full">Arsipkan</Button>
              <Button size="sm" variant="outline" data-testid="bulk-delete" onClick={() => changeStatus(selected, "dihapus")} className="rounded-full text-red-600 border-red-200">Hapus</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected([])} className="rounded-full">Batal</Button>
            </div>
          )}
          <div className="overflow-x-auto rounded-2xl border border-border bg-white">
            <table className="w-full text-sm" data-testid="admin-vehicle-table">
              <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3 w-10"><Checkbox data-testid="select-all" checked={filteredVehicles.length > 0 && selected.length === filteredVehicles.length} onCheckedChange={toggleAll} /></th><th className="p-3">Merek/Model</th><th className="p-3">Kategori</th><th className="p-3">Harga</th><th className="p-3">Kelengkapan</th><th className="p-3">Status</th><th className="p-3">Aksi</th></tr></thead>
              <tbody>
                {filteredVehicles.map((v) => (
                  <tr key={v.vehicle_id} className="border-b border-border last:border-0" data-testid={`admin-vehicle-${v.vehicle_id}`}>
                    <td className="p-3"><Checkbox data-testid={`select-${v.vehicle_id}`} checked={selected.includes(v.vehicle_id)} onCheckedChange={() => toggleSelect(v.vehicle_id)} /></td>
                    <td className="p-3 font-medium text-navy">{v.brand} {v.model}</td>
                    <td className="p-3 text-muted-foreground">{v.category}</td>
                    <td className="p-3">{formatRupiah(v.official_price)}</td>
                    <td className="p-3 w-44">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className={`h-full ${(v.specification_completeness ?? 0) >= 70 ? "bg-teal" : (v.specification_completeness ?? 0) >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${v.specification_completeness ?? 0}%` }} /></div>
                        <span className="text-xs font-semibold text-navy w-9 text-right" data-testid={`completeness-${v.vehicle_id}`}>{v.specification_completeness ?? 0}%</span>
                        {v.ai_drafted && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700" data-testid={`ai-draft-badge-${v.vehicle_id}`}>AI</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      {vehicleStatusView === "dihapus" ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-600">Terhapus</span>
                      ) : (
                        <Select value={v.vehicle_status === "arsip" ? "arsip" : "aktif"} onValueChange={(s) => changeStatus([v.vehicle_id], s)}>
                          <SelectTrigger data-testid={`status-${v.vehicle_id}`} className="rounded-full h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white"><SelectItem value="aktif">Aktif</SelectItem><SelectItem value="arsip">Arsip</SelectItem></SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="p-3 flex gap-1">
                      {vehicleStatusView === "dihapus" ? (
                        <Button size="sm" data-testid={`admin-restore-${v.vehicle_id}`} onClick={() => changeStatus([v.vehicle_id], "aktif")} className="rounded-full bg-teal hover:bg-teal-hover text-xs h-8">Pulihkan</Button>
                      ) : (
                        <>
                          <Button size="icon" variant="ghost" data-testid={`admin-edit-${v.vehicle_id}`} onClick={() => openEdit(v)}><Pencil className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" data-testid={`admin-delete-${v.vehicle_id}`} onClick={() => setArchiveId(v.vehicle_id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-sm text-muted-foreground">Asal akun:</span>
            <Select value={userOrigin} onValueChange={setUserOrigin}>
              <SelectTrigger data-testid="user-origin-filter" className="rounded-xl h-10 w-56"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="organik">Organik ({userCounts.organik})</SelectItem>
                <SelectItem value="emergent">Emergent / Uji ({userCounts.emergent})</SelectItem>
                <SelectItem value="all">Semua</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">Peran:</span>
            <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
              <SelectTrigger data-testid="user-role-filter" className="rounded-xl h-10 w-44"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Pengguna</SelectItem>
                <SelectItem value="admin">Admin & Editor</SelectItem>
                <SelectItem value="user">Pengguna Biasa</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">Segmen:</span>
            <Select value={userSegment} onValueChange={setUserSegment}>
              <SelectTrigger data-testid="user-segment-filter" className="rounded-xl h-10 w-44"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {SEGMENT_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {userOrigin === "emergent" && users.length > 0 && isSuper && (
              <Button variant="destructive" size="sm" data-testid="delete-emergent-btn" onClick={() => setShowDeleteEmergent(true)} className="rounded-full">Hapus semua akun emergent</Button>
            )}
            <div className="relative ml-auto">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="admin-user-search" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Cari nama atau email…" className="rounded-full pl-9 h-10 w-64" />
            </div>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-border bg-white">
            <table className="w-full text-sm" data-testid="admin-user-table">
              <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Peran</th><th className="p-3">Asal</th><th className="p-3">Trial Dipakai</th><th className="p-3">Paket</th><th className="p-3">Langganan</th><th className="p-3">Terdaftar</th><th className="p-3">Aktivitas Terakhir</th><th className="p-3">Pembayaran</th><th className="p-3">Aksi</th></tr></thead>
              <tbody>
                {users.filter((u) => (userRoleFilter === "all" || (userRoleFilter === "admin" ? ["admin", "editor"].includes(u.role) : !["admin", "editor"].includes(u.role))) && (!userSearch || `${u.name} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase()))).map((u) => (
                  <tr key={u.user_id} className="border-b border-border last:border-0">
                    <td className="p-3 font-medium text-navy">{u.name}</td>
                    <td className="p-3 text-muted-foreground">{u.email}</td>
                    <td className="p-3">
                      {(isSuper && u.role !== "super_admin") ? (
                        <Select value={u.role} onValueChange={(r) => { if (r !== u.role) setRoleChange({ userId: u.user_id, role: r, name: u.name }); }}>
                          <SelectTrigger data-testid={`role-${u.user_id}`} className="rounded-full h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white"><SelectItem value="free">Free</SelectItem><SelectItem value="premium">Premium</SelectItem><SelectItem value="editor">Editor</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                        </Select>
                      ) : (
                        <span data-testid={`role-${u.user_id}`} className="text-xs px-2 py-1 rounded-full bg-secondary text-navy capitalize">{u.role === "super_admin" ? "Super Admin" : u.role}</span>
                      )}
                    </td>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.origin === "emergent" ? "bg-amber-100 text-amber-700" : "bg-teal/10 text-teal"}`} data-testid={`origin-${u.user_id}`}>{u.origin === "emergent" ? "Emergent" : "Organik"}</span></td>
                    <td className="p-3" data-testid={`trial-count-${u.user_id}`}>{u.trial_count ?? 0}</td>
                    <td className="p-3 text-xs">{u.current_plan || "-"}</td>
                    <td className="p-3">{u.subscription_status_label === "active" ? <span className="text-xs px-2 py-0.5 rounded-full bg-teal/10 text-teal font-semibold">Aktif</span> : u.subscription_status_label === "expired" ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600">Expired</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                    <td className="p-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("id-ID") : "-"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{u.last_activity_at ? new Date(u.last_activity_at).toLocaleDateString("id-ID") : "-"}</td>
                    <td className="p-3">{u.payment_status === "paid" ? <span className="text-xs px-2 py-0.5 rounded-full bg-teal/10 text-teal font-semibold">Paid</span> : u.payment_status === "pending" ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                    <td className="p-3 flex gap-1">
                      <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`admin-trial-${u.user_id}`} onClick={() => addTrial(u.user_id)}>+3 Trial</Button>
                      <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`admin-premium30-${u.user_id}`} onClick={() => grantPremium(u.user_id, 30)}>+30 Hari</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AlertDialog open={showDeleteEmergent} onOpenChange={setShowDeleteEmergent}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus semua akun emergent?</AlertDialogTitle>
                <AlertDialogDescription>{userCounts.emergent} akun buatan sistem/pengujian akan dihapus permanen. Pengguna organik tidak terpengaruh.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-emergent-cancel">Batal</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-emergent-confirm" onClick={deleteEmergent} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog open={!!roleChange} onOpenChange={(o) => !o && setRoleChange(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Ubah peran pengguna?</AlertDialogTitle>
                <AlertDialogDescription>Ubah peran <b>{roleChange?.name}</b> menjadi <b>{roleChange?.role}</b>? Perubahan langsung berlaku.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="role-change-cancel">Batal</AlertDialogCancel>
                <AlertDialogAction data-testid="role-change-confirm" onClick={() => { if (roleChange) setRole(roleChange.userId, roleChange.role); setRoleChange(null); }}>Ya, ubah</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="reports">
          <div className="space-y-8">
            <div>
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                  <h3 className="font-heading font-bold text-navy mb-1">Funnel Konversi</h3>
                  <p className="text-sm text-muted-foreground">Unique user per tahap, dari database internal. Gunakan filter cohort agar semua tahap berasal dari kelompok registrasi yang sama.</p>
                </div>
                <div className="ml-auto flex items-end gap-2 flex-wrap">
                  <div><Label className="text-xs">Registrasi dari</Label><Input data-testid="cohort-start" type="date" value={cohortStart} onChange={(e) => setCohortStart(e.target.value)} className="rounded-xl h-10 mt-1" /></div>
                  <div><Label className="text-xs">sampai</Label><Input data-testid="cohort-end" type="date" value={cohortEnd} onChange={(e) => setCohortEnd(e.target.value)} className="rounded-xl h-10 mt-1" /></div>
                  <Button data-testid="cohort-apply" onClick={() => loadFunnel()} className="rounded-full bg-teal hover:bg-teal-hover h-10">Terapkan</Button>
                  {(cohortStart || cohortEnd) && <Button variant="ghost" data-testid="cohort-reset" onClick={() => { setCohortStart(""); setCohortEnd(""); loadFunnel("", ""); }} className="rounded-full h-10">Reset</Button>}
                </div>
              </div>
              {funnel?.cohort?.start || funnel?.cohort?.end ? (
                <p className="text-xs font-semibold text-teal mb-3" data-testid="cohort-active">Cohort aktif: registrasi {funnel.cohort.start || "…"} – {funnel.cohort.end || "…"} ({funnel.registrations} user)</p>
              ) : null}
              {funnel ? (
                <div className="space-y-6">
                  <FunnelSection title="A. Activation" testId="funnel-activation" base={funnel.registrations} steps={[
                    { l: "Registrasi", v: funnel.registrations },
                    { l: "Trial 1", v: funnel.trial_1 ?? 0 },
                    { l: "Trial 2", v: funnel.trial_2 ?? 0 },
                  ]} />
                  <FunnelSection title="B. Monetization" testId="funnel-monetization" base={funnel.registrations} steps={[
                    { l: "Trial 2", v: funnel.trial_2 ?? 0 },
                    { l: "Offer 3 Hari", v: funnel.offer_3_day ?? 0 },
                    { l: "Klik Offer", v: funnel.offer_clicked_3_day ?? 0 },
                    { l: "Mulai Checkout", v: funnel.checkout_3_day ?? 0 },
                    { l: "Bayar Paket 3 Hari", v: funnel.paid_3_day ?? 0 },
                  ]} />
                  <FunnelSection title="C. Premium / Upgrade" testId="funnel-premium" base={funnel.registrations} steps={[
                    { l: "Lihat Paywall", v: funnel.paywall_viewed ?? 0 },
                    { l: "Pilih Paket", v: funnel.plan_selected ?? 0 },
                    { l: "Mulai Checkout Premium", v: funnel.premium_checkout ?? 0 },
                    { l: "Bayar Premium", v: funnel.paid_premium ?? 0 },
                  ]} />
                  <div data-testid="funnel-legacy">
                    <p className="text-sm font-semibold text-navy mb-2">Historical</p>
                    <Card className="rounded-2xl p-5 inline-block min-w-44" data-testid="funnel-Trial 3 (Legacy)">
                      <p className="text-sm text-muted-foreground">Trial 3 (Legacy)</p>
                      <p className="font-heading font-extrabold text-2xl text-navy">{funnel.trial_3 ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Metrik lama, bukan funnel user baru</p>
                    </Card>
                  </div>
                  {funnel.segments && (
                    <div data-testid="segments-summary">
                      <p className="text-sm font-semibold text-navy mb-2">Segmentasi Pengguna (organik, tanpa staf)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {[["registered_only", "Registered Only"], ["trial_1", "Trial 1"], ["trial_2_plus", "Trial 2+"], ["three_day_buyer", "3-Day Buyer"], ["premium", "Premium"], ["expired", "Expired"]].map(([k, l]) => (
                          <Card key={k} className="rounded-2xl p-4" data-testid={`segment-${k}`}>
                            <p className="text-xs text-muted-foreground">{l}</p>
                            <p className="font-heading font-extrabold text-xl text-navy">{funnel.segments[k] ?? 0}</p>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                  {anomalies && anomalies.total > 0 && (
                    <div data-testid="anomalies-section">
                      <p className="text-sm font-semibold text-red-600 mb-2">Anomali Data ({anomalies.total}) — untuk review, tidak dihapus otomatis</p>
                      <div className="overflow-x-auto rounded-2xl border border-red-200 bg-red-50/40">
                        <table className="w-full text-sm" data-testid="anomalies-table">
                          <thead><tr className="text-left text-muted-foreground border-b border-red-200"><th className="p-3">Tipe</th><th className="p-3">Email</th><th className="p-3">Detail</th></tr></thead>
                          <tbody>
                            {anomalies.items.map((a, i) => (
                              <tr key={i} className="border-b border-red-100" data-testid={`anomaly-${i}`}>
                                <td className="p-3 font-medium text-red-700">{a.type}</td>
                                <td className="p-3">{a.email || a.user_id}</td>
                                <td className="p-3 text-muted-foreground">{a.detail}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : <p className="text-sm text-muted-foreground">Memuat…</p>}
              <p className="text-xs text-muted-foreground mt-2">Total pengguna organik terdaftar: {funnel?.total_users ?? 0}</p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-navy mb-1">Pencarian Tanpa Hasil</h3>
              <p className="text-sm text-muted-foreground mb-3">Merek/tipe yang dicari pengunjung tetapi belum ada di database. Prioritaskan menambahkan data ini untuk menaikkan konversi.</p>
              {misses.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada data pencarian tanpa hasil.</p> : (
                <div className="overflow-x-auto rounded-2xl border">
                  <table className="w-full text-sm" data-testid="search-misses-table">
                    <thead><tr className="text-left text-muted-foreground border-b bg-secondary"><th className="p-3">Kata Pencarian</th><th className="p-3">Jumlah Dicari</th><th className="p-3">Kategori Terakhir</th><th className="p-3">Terakhir</th><th className="p-3">Aksi</th></tr></thead>
                    <tbody>
                      {misses.map((m, i) => (
                        <tr key={i} className="border-b" data-testid={`search-miss-${i}`}>
                          <td className="p-3 font-medium text-navy">{m.query}</td>
                          <td className="p-3">{m.count}</td>
                          <td className="p-3">{m.last_category || "-"}</td>
                          <td className="p-3 text-muted-foreground">{m.last_at ? new Date(m.last_at).toLocaleDateString("id-ID") : "-"}</td>
                          <td className="p-3"><Button size="sm" variant="outline" data-testid={`draft-miss-${i}`} disabled={drafting === m.query} onClick={() => aiDraft(m.query)} className="rounded-full text-xs h-8 whitespace-nowrap">Buat Draft AI</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <Card className="rounded-2xl p-6">
            <h3 className="font-heading font-bold text-lg text-navy mb-4">Riwayat Perubahan Peran</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="p-3">Waktu</th><th className="p-3">Diubah oleh</th><th className="p-3">Pengguna</th><th className="p-3">Perubahan</th></tr></thead>
                <tbody>
                  {auditLogs.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Belum ada perubahan peran.</td></tr>}
                  {auditLogs.map((l) => (
                    <tr key={l.log_id} className="border-b border-border/50" data-testid={`audit-row-${l.log_id}`}>
                      <td className="p-3 whitespace-nowrap">{new Date(l.created_at).toLocaleString("id-ID")}</td>
                      <td className="p-3">{l.actor?.name || l.actor?.email || "-"}</td>
                      <td className="p-3">{l.target_user?.name || l.target_user?.email || l.target}</td>
                      <td className="p-3"><span className="capitalize text-muted-foreground">{l.detail?.from || "?"}</span> → <span className="font-semibold capitalize text-navy">{l.detail?.to || "?"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="rounded-2xl p-6">
              <h3 className="font-heading font-bold text-navy mb-4">Tarif Energi</h3>
              {tariffs && (
                <div className="space-y-3">
                  {(tariffs.fuels || []).map((fu, i) => (
                    <div key={fu.fuel_type}><Label>Harga {fu.fuel_type} /liter</Label><Input data-testid={`tariff-fuel-${i}`} type="number" value={fu.price_per_liter || ""} onChange={(e) => { const nf = [...tariffs.fuels]; nf[i] = { ...fu, price_per_liter: e.target.value }; setTariffs({ ...tariffs, fuels: nf }); }} className="rounded-xl h-11 mt-1" /></div>
                  ))}
                  <div><Label>Tarif Listrik /kWh</Label><Input data-testid="tariff-elec" type="number" value={tariffs.electricity?.price_per_kwh || ""} onChange={(e) => setTariffs({ ...tariffs, electricity: { ...tariffs.electricity, price_per_kwh: e.target.value } })} className="rounded-xl h-11 mt-1" /></div>
                  <div><Label>Tarif Charge Publik /kWh</Label><Input data-testid="tariff-public" type="number" value={tariffs.public_charging?.price_per_kwh || ""} onChange={(e) => setTariffs({ ...tariffs, public_charging: { ...tariffs.public_charging, price_per_kwh: e.target.value } })} className="rounded-xl h-11 mt-1" /></div>
                  <Button data-testid="tariff-save" onClick={saveTariffs} className="rounded-full bg-teal hover:bg-teal-hover w-full">Simpan Tarif</Button>
                </div>
              )}
            </Card>
            <Card className="rounded-2xl p-6">
              <h3 className="font-heading font-bold text-navy mb-4">Paket Berbayar</h3>
              <div className="space-y-4">
                {plans.map((p, idx) => (
                  <div key={p.plan_id} className="border border-border rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-navy">{p.name}</p>
                      {p.offer_type === "post_trial" && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`plan-badge-${p.plan_id}`}>Post-Trial Offer</span>}
                      {p.public === false && <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Tidak tampil di pricing umum</span>}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid={`plan-price-${p.plan_id}`} type="number" value={p.price} onChange={(e) => { const np = [...plans]; np[idx] = { ...p, price: e.target.value }; setPlans(np); }} className="rounded-xl h-10" placeholder="Harga" />
                      <Input type="number" value={p.duration_days} onChange={(e) => { const np = [...plans]; np[idx] = { ...p, duration_days: e.target.value }; setPlans(np); }} className="rounded-xl h-10" placeholder="Hari" />
                    </div>
                    <Button size="sm" data-testid={`plan-save-${p.plan_id}`} onClick={() => savePlan(plans[idx])} className="rounded-full bg-navy hover:bg-navy/90 w-full">Simpan</Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Vehicle dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-vehicle-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Kendaraan" : "Tambah Kendaraan"}</DialogTitle></DialogHeader>
          <div className="mb-1">
            <Label>Gambar Kendaraan</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.image ? <img src={form.image} alt="preview" className="w-20 h-20 rounded-xl object-cover border" data-testid="vf-image-preview" /> : <div className="w-20 h-20 rounded-xl bg-secondary flex items-center justify-center text-xs text-muted-foreground">Tanpa foto</div>}
              <div className="flex-1 space-y-2">
                <label><input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="vf-image-input" /><span className="inline-flex items-center cursor-pointer rounded-full border border-border px-4 h-9 text-sm font-medium hover:bg-secondary">{uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />Unggah Gambar</>}</span></label>
                <Input data-testid="vf-image-url" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-xl h-9" placeholder="atau tempel URL gambar" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <VF k="brand" label="Merek*" type="text" form={form} setForm={setForm} />
            <VF k="model" label="Model*" type="text" form={form} setForm={setForm} />
            <VF k="variant" label="Varian" type="text" form={form} setForm={setForm} />
            <div><Label className="text-xs">Kategori*</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="vf-category" className="rounded-xl h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="motor_listrik">Motor Listrik</SelectItem><SelectItem value="sepeda_listrik">Sepeda Listrik</SelectItem><SelectItem value="motor_bensin">Motor Bensin</SelectItem></SelectContent>
              </Select>
            </div>
            <VF k="production_year" label="Tahun produksi" form={form} setForm={setForm} />
            <VF k="official_price" label="Harga resmi (Rp)" form={form} setForm={setForm} />
            {form.category !== "motor_bensin" ? (
              <>
                <VF k="motor_nominal_w" label="Daya nominal (W)" form={form} setForm={setForm} />
                <VF k="motor_peak_w" label="Daya puncak (W)" form={form} setForm={setForm} />
                <VF k="motor_torque_nm" label="Torsi motor (Nm)" form={form} setForm={setForm} />
                <VF k="battery_capacity_kwh" label="Kapasitas baterai (kWh)" form={form} setForm={setForm} />
                <VF k="battery_voltage_v" label="Tegangan baterai (V)" form={form} setForm={setForm} />
                <VF k="battery_capacity_ah" label="Kapasitas baterai (Ah)" form={form} setForm={setForm} />
                <VF k="battery_chemistry" label="Kimia baterai" type="text" form={form} setForm={setForm} />
                <div><Label className="text-xs">Skema baterai</Label>
                  <Select value={form.battery_scheme} onValueChange={(v) => setForm({ ...form, battery_scheme: v })}>
                    <SelectTrigger data-testid="vf-battery-scheme" className="rounded-xl h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="beli">Beli</SelectItem><SelectItem value="sewa">Sewa</SelectItem><SelectItem value="swap">Swap</SelectItem></SelectContent>
                  </Select>
                </div>
                <VF k="monthly_battery_rental" label="Sewa baterai/bulan (Rp)" form={form} setForm={setForm} />
                <VF k="claimed_range_km" label="Jarak klaim (km)" form={form} setForm={setForm} />
                <VF k="tested_range_min_km" label="Jarak uji min (km)" form={form} setForm={setForm} />
                <VF k="tested_range_max_km" label="Jarak uji maks (km)" form={form} setForm={setForm} />
                <VF k="efficiency_wh_per_km" label="Konsumsi (Wh/km)" form={form} setForm={setForm} />
                <VF k="top_speed_kmh" label="Kecepatan maks (km/jam)" form={form} setForm={setForm} />
                <VF k="charge_time_hours" label="Waktu charge (jam)" form={form} setForm={setForm} />
                <div className="flex items-center gap-2 mt-6"><Checkbox data-testid="vf-removable" checked={!!form.removable_battery} onCheckedChange={(c) => setForm({ ...form, removable_battery: !!c })} /><Label className="text-xs">Baterai dapat dilepas</Label></div>
              </>
            ) : (
              <>
                <VF k="engine_cc" label="Kapasitas mesin (cc)" form={form} setForm={setForm} />
                <VF k="fuel_type" label="Jenis BBM" type="text" form={form} setForm={setForm} />
                <VF k="efficiency_km_per_liter" label="Konsumsi (km/l)" form={form} setForm={setForm} />
                <VF k="top_speed_kmh" label="Kecepatan maks (km/jam)" form={form} setForm={setForm} />
                <VF k="annual_service_cost" label="Biaya servis/tahun (Rp)" form={form} setForm={setForm} />
              </>
            )}
            <VF k="curb_weight_kg" label="Berat (kg)" form={form} setForm={setForm} />
            <VF k="maximum_payload_kg" label="Beban maks (kg)" form={form} setForm={setForm} />
            <VF k="length_mm" label="Panjang (mm)" form={form} setForm={setForm} />
            <VF k="width_mm" label="Lebar (mm)" form={form} setForm={setForm} />
            <VF k="height_mm" label="Tinggi (mm)" form={form} setForm={setForm} />
            <VF k="vehicle_warranty" label="Garansi kendaraan" type="text" form={form} setForm={setForm} />
            {form.category !== "motor_bensin" && <VF k="battery_warranty" label="Garansi baterai" type="text" form={form} setForm={setForm} />}
            <VF k="service_coverage" label="Cakupan servis" type="text" form={form} setForm={setForm} />
            <div className="col-span-2 border-t border-border pt-3 mt-1">
              <div className="flex items-center justify-between">
                <Label>Kelengkapan data (otomatis)</Label>
                <span className="text-sm font-bold text-teal" data-testid="vf-completeness-auto">{autoCompleteness}%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Dihitung dari jumlah kolom inti yang terisi untuk kategori ini. Dimensi (P×L×T) dipakai untuk estimasi berat pada Cek Tanjakan bila berat resmi kosong.</p>
              <label className="flex items-center gap-2 mt-2 text-sm text-navy cursor-pointer">
                <Checkbox data-testid="vf-completeness-manual" checked={!!form.completeness_manual} onCheckedChange={(c) => setForm({ ...form, completeness_manual: !!c })} />
                Atur manual (timpa nilai otomatis)
              </label>
              {form.completeness_manual && (
                <Input data-testid="vf-completeness-input" type="number" min="0" max="100" value={form.specification_completeness} onChange={(e) => setForm({ ...form, specification_completeness: e.target.value === "" ? "" : Number(e.target.value) })} className="rounded-xl h-10 mt-2 w-32" placeholder="0-100" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Batal</Button>
            <Button data-testid="vf-save" onClick={save} disabled={saving} className="rounded-full bg-teal hover:bg-teal-hover">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveId} onOpenChange={(o) => !o && setArchiveId(null)}>
        <AlertDialogContent className="bg-white" data-testid="admin-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus kendaraan ini?</AlertDialogTitle>
            <AlertDialogDescription>Kendaraan akan dihapus dan tidak lagi muncul untuk pengguna maupun di daftar admin. Berbeda dari "Arsip", data yang dihapus disembunyikan sepenuhnya.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="admin-archive-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="admin-archive-confirm" onClick={() => archive(archiveId)}>Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDemo} onOpenChange={setShowDeleteDemo}>
        <AlertDialogContent className="bg-white" data-testid="delete-demo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus semua data demo?</AlertDialogTitle>
            <AlertDialogDescription>Semua kendaraan berlabel DATA DEMO akan dihapus permanen. Lakukan ini setelah Anda mengimpor data nyata terverifikasi. Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-demo-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="delete-demo-confirm" onClick={deleteDemo}>Hapus Semua Demo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function VF({ k, label, type = "number", form, setForm }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        data-testid={`vf-${k}`}
        type={type}
        value={form[k]}
        onChange={(e) => setForm((f) => ({ ...f, [k]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value }))}
        className="rounded-xl h-10 mt-1"
      />
    </div>
  );
}
